import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Plus, CheckCircle, XCircle, Calendar, Sparkles } from "lucide-react";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { DataTable } from "../components/dataTable";
import { createEmployeeLeaveColumns, Leave } from "../lib/leavesTableColumns";

interface LeaveSummary {
  totalPaidLeaves: number;
  usedPaidLeaves: number;
  pendingPaidLeaves: number;
  remainingPaidLeaves: number;
  usedUnpaidLeaves: number;
  compOffSummary?: {
    available: number;
    used: number;
    pending: number;
    expiringSoon?: number;
  };
}

export default function EmployeeLeaves() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leaves, setLeaves] = useState<Leave[]>([]);
  const [summary, setSummary] = useState<LeaveSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState({}, document.title);

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchLeaves();
  }, [statusFilter]);

  const fetchLeaves = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/leaves/my-leaves?status=${statusFilter}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const normalized = (response.data.leaves || []).map((leave: any) => ({
        ...leave,
        id: leave._id,
      }));

      setLeaves(normalized);
      setSummary(response.data.summary);
    } catch (error: any) {
      console.error("Error fetching leaves:", error);
      setError(error.response?.data?.message || "Failed to fetch leaves");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setIsViewDialogOpen(true);
  };

  const handleCancelClick = (leave: Leave) => {
    setSelectedLeave(leave);
    setIsCancelDialogOpen(true);
  };

  const handleCancelLeave = async () => {
    if (!selectedLeave) return;

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("authToken");

      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}api/leaves/${selectedLeave._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setIsCancelDialogOpen(false);
      setSelectedLeave(null);
      setSuccessMessage("Leave application cancelled successfully!");
      fetchLeaves();
    } catch (error: any) {
      console.error("Error cancelling leave:", error);
      setError(
        error.response?.data?.message || "Failed to cancel leave application"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = createEmployeeLeaveColumns(
    handleCancelClick,
    handleViewClick
  );

  const compOffSummary = summary?.compOffSummary || {
    available: 0,
    pending: 0,
    used: 0,
    expiringSoon: 0,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your leaves...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Leave Applications</h1>
          <p className="text-muted-foreground mt-1">
            View and manage your leave requests
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/employee/compoff")}>
            <Sparkles className="mr-2 h-4 w-4" />
            Comp-Offs
          </Button>
          <Button onClick={() => navigate("/employee/leaves/apply")}>
            <Plus className="mr-2 h-4 w-4" />
            Apply for Leave
          </Button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage("")}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">{error}</p>
          </div>
          <button
            onClick={() => setError("")}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Leave Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Paid Leaves
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                <span className="text-2xl font-bold">
                  {summary.totalPaidLeaves}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Used Paid Leaves
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">
                  {summary.usedPaidLeaves}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Remaining Paid Leaves
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <span className="text-2xl font-bold">
                  {summary.remainingPaidLeaves}
                </span>
              </div>
              {summary.pendingPaidLeaves > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {summary.pendingPaidLeaves} days pending approval
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Used Unpaid Leaves
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-gray-600" />
                <span className="text-2xl font-bold">
                  {summary.usedUnpaidLeaves}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Comp-Off Credits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <span className="text-2xl font-bold">
                  {compOffSummary.available}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {compOffSummary.pending} pending • {compOffSummary.used} used
              </p>
              {compOffSummary.expiringSoon ? (
                <p className="text-xs text-red-600 mt-1">
                  {compOffSummary.expiringSoon} credit(s) expiring soon
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leaves</SelectItem>
            <SelectItem value="Pending">Pending</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leaves Table */}
      <DataTable columns={columns} data={leaves} />

      {/* View Leave Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Leave Application Details</DialogTitle>
            <DialogDescription>
              Complete information about your leave request
            </DialogDescription>
          </DialogHeader>

          {selectedLeave && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Leave Type</Label>
                  <p className="font-medium mt-1">{selectedLeave.leaveType}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Mode</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedLeave.leaveMode === "comp-off" ||
                        selectedLeave.compOffId
                          ? "bg-purple-100 text-purple-800"
                          : "bg-blue-100 text-blue-800"
                      }
                    >
                      {selectedLeave.leaveMode === "comp-off" ||
                      selectedLeave.compOffId
                        ? "Comp-Off"
                        : "Regular"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedLeave.isPaidLeave
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }
                    >
                      {selectedLeave.isPaidLeave ? "Paid" : "Unpaid"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Start Date</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedLeave.startDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">End Date</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedLeave.endDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">
                    Number of Days
                  </Label>
                  <p className="font-medium mt-1">
                    {selectedLeave.numberOfDays}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge
                      className={
                        selectedLeave.status === "Approved"
                          ? "bg-green-100 text-green-800"
                          : selectedLeave.status === "Rejected"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }
                    >
                      {selectedLeave.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Applied Date</Label>
                  <p className="font-medium mt-1">
                    {new Date(selectedLeave.appliedDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {(selectedLeave.leaveMode === "comp-off" ||
                selectedLeave.compOffId ||
                selectedLeave.compOff) && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-purple-50 rounded-md border border-purple-100">
                  <div>
                    <Label className="text-muted-foreground">Comp-Off Earned</Label>
                    <p className="font-medium mt-1">
                      {selectedLeave.compOff?.workDate
                        ? new Date(
                            selectedLeave.compOff.workDate
                          ).toLocaleDateString()
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Credit Days</Label>
                    <p className="font-medium mt-1">
                      {selectedLeave.compOff?.creditDays ?? "—"}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-muted-foreground">Reason</Label>
                <p className="mt-1 p-3 bg-muted rounded-md">
                  {selectedLeave.reason}
                </p>
              </div>

              {selectedLeave.rejectionReason && (
                <div>
                  <Label className="text-muted-foreground text-red-600">
                    Rejection Reason
                  </Label>
                  <p className="mt-1 p-3 bg-red-50 text-red-800 rounded-md border border-red-200">
                    {selectedLeave.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Leave Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Leave Application</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this leave application? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {selectedLeave && (
            <div className="p-3 bg-muted rounded-md space-y-2">
              <p>
                <span className="font-medium">Leave Type:</span>{" "}
                {selectedLeave.leaveType}
              </p>
              <p>
                <span className="font-medium">Duration:</span>{" "}
                {new Date(selectedLeave.startDate).toLocaleDateString()} -{" "}
                {new Date(selectedLeave.endDate).toLocaleDateString()} (
                {selectedLeave.numberOfDays} days)
              </p>
              <p>
                <span className="font-medium">Status:</span>{" "}
                {selectedLeave.status}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCancelDialogOpen(false)}
              disabled={isDeleting}
            >
              No, Keep It
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelLeave}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Spinner variant="ellipsis" size={16} className="mr-2" />
                  Cancelling...
                </>
              ) : (
                <>
                  <XCircle className="mr-2 h-4 w-4" />
                  Yes, Cancel Leave
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
